import React, { useState, useEffect, useRef } from 'react';
import { Box, Flex, Text, Icon, useBreakpointValue } from '@chakra-ui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare } from 'lucide-react';

interface UnifiedCaptionsProps {
  text: string;
  isSpeaking?: boolean;
  hudStyle?: 'dark' | 'light';
}

const MotionBox = motion(Box);
const MotionText = motion(Text);

/**
 * A unified captions component that provides consistent captions experience
 * across all slides in the presentation.
 */
const UnifiedCaptions: React.FC<UnifiedCaptionsProps> = ({ 
  text = '', 
  isSpeaking = false,
  hudStyle = 'dark'
}) => {
  const [phrases, setPhrases] = useState<string[]>([]);
  const [displayedPhrases, setDisplayedPhrases] = useState<string[]>([]);
  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Responsive positioning and sizing
  const captionsPosition = useBreakpointValue({ 
    base: { bottom: "10px", width: "80%", right: "10px" },
    sm: { bottom: "15px", width: "75%", right: "15px" },
    md: { bottom: "20px", width: "70%", right: "20px" },
    lg: { bottom: "25px", width: "60%", right: "25px" }
  }) || { bottom: "20px", width: "70%", right: "20px" };
  
  const captionsFontSize = useBreakpointValue({ 
    base: "xs", 
    sm: "sm", 
    md: "md" 
  }) || "md";
  
  // HUD style configurations
  const hudStyles = {
    dark: {
      bg: "rgba(0, 0, 0, 0.65)", // Slightly more transparent
      textColor: "white",
      accentColor: "blue.300",
      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.4)",
      backdropFilter: "blur(8px)"
    },
    light: {
      bg: "rgba(255, 255, 255, 0.75)", // Slightly more transparent
      textColor: "gray.800",
      accentColor: "blue.500",
      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
      backdropFilter: "blur(8px)"
    }
  };
  
  const currentHudStyle = hudStyles[hudStyle];

  // Split text into phrases when text changes
  useEffect(() => {
    if (!text) {
      setPhrases([]);
      setDisplayedPhrases([]);
      setCurrentPhraseIndex(0);
      return;
    }

    // Split by natural phrase breaks (commas, semicolons, periods, etc.)
    const phraseDelimiters = /([,;:.!?]\s|\n|\r|\s-\s)/;
    const phraseArray = text.split(phraseDelimiters).filter(phrase => phrase.trim().length > 0);
    
    // Group very short phrases together
    const groupedPhrases: string[] = [];
    let currentGroup = "";
    
    phraseArray.forEach(phrase => {
      if (currentGroup.length + phrase.length < 50) {
        currentGroup += phrase;
      } else {
        if (currentGroup) {
          groupedPhrases.push(currentGroup.trim());
        }
        currentGroup = phrase;
      }
    });
    
    if (currentGroup) {
      groupedPhrases.push(currentGroup.trim());
    }
    
    setPhrases(groupedPhrases);
    setDisplayedPhrases([]);
    setCurrentPhraseIndex(0);
  }, [text]);

  // Handle phrase-by-phrase display
  useEffect(() => {
    if (phrases.length === 0 || currentPhraseIndex >= phrases.length || !isSpeaking) return;

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set timeout for next phrase - natural reading cadence (1.5-2.5 seconds per phrase)
    const phrase = phrases[currentPhraseIndex];
    const phraseLength = phrase.length;
    // Adjust timing based on phrase length (longer phrases display longer)
    const displayTime = Math.max(1500, Math.min(2500, 1500 + phraseLength * 20));
    
    timeoutRef.current = setTimeout(() => {
      setCurrentPhraseIndex(prev => {
        const nextIndex = prev + 1;
        if (nextIndex < phrases.length) {
          // Add the next phrase to displayed phrases
          setDisplayedPhrases(prevPhrases => {
            // Keep only the last 3 phrases for a cleaner display
            const newPhrases = [...prevPhrases, phrases[nextIndex]];
            if (newPhrases.length > 3) {
              return newPhrases.slice(newPhrases.length - 3);
            }
            return newPhrases;
          });
          return nextIndex;
        }
        return prev;
      });
    }, displayTime);

    // Add the first phrase or when resetting
    if (currentPhraseIndex === 0 || displayedPhrases.length === 0) {
      setDisplayedPhrases([phrase]);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [phrases, currentPhraseIndex, isSpeaking, displayedPhrases]);

  // Reset captions when speaking stops
  useEffect(() => {
    if (!isSpeaking && phrases.length > 0) {
      // Reset to the first phrase when speaking stops
      setCurrentPhraseIndex(0);
      setDisplayedPhrases([]);
    }
  }, [isSpeaking, phrases]);

  return (
    <AnimatePresence>
      {text && (
        <MotionBox
          position="fixed"
          bottom={captionsPosition.bottom}
          right={captionsPosition.right}
          transform="translateX(0%)"
          width={captionsPosition.width}
          maxW="900px"
          backgroundColor={currentHudStyle.bg}
          backdropFilter={currentHudStyle.backdropFilter}
          borderRadius="xl"
          boxShadow={currentHudStyle.boxShadow}
          p={{ base: 3, md: 4 }}
          zIndex={100}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.4 }}
          _before={{
            content: '""',
            position: "absolute",
            top: "-10px",
            left: "5%",
            right: "5%",
            height: "10px",
            background: hudStyle === 'dark' 
              ? "linear-gradient(to bottom, rgba(255,255,255,0.1), rgba(255,255,255,0))"
              : "linear-gradient(to bottom, rgba(0,0,0,0.05), rgba(0,0,0,0))",
            borderRadius: "50%",
            filter: "blur(5px)",
            zIndex: -1
          }}
          _after={{
            content: '""',
            position: "absolute",
            bottom: "-15px",
            left: "10%",
            right: "10%",
            height: "15px",
            background: hudStyle === 'dark' ? "rgba(0,0,0,0.2)" : "rgba(0,0,0,0.1)",
            borderRadius: "50%",
            filter: "blur(10px)",
            zIndex: -1
          }}
        >
          <Flex align="center" mb={2} opacity={0.8}>
            <Icon as={MessageSquare} color={currentHudStyle.accentColor} boxSize="14px" mr={1} />
            <Text color={currentHudStyle.accentColor} fontWeight="medium" fontSize="xs" letterSpacing="wide">
              CAPTIONS
            </Text>
          </Flex>
          
          <Box>
            {displayedPhrases.map((phrase, index) => (
              <MotionText
                key={`${index}-${phrase.substring(0, 10)}`}
                color={currentHudStyle.textColor}
                fontSize={captionsFontSize}
                fontWeight="normal"
                lineHeight="1.6"
                letterSpacing="0.02em"
                textShadow={hudStyle === 'dark' ? "0 1px 2px rgba(0, 0, 0, 0.5)" : "none"}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                mb={index < displayedPhrases.length - 1 ? 1 : 0}
              >
                {phrase}
                {index === displayedPhrases.length - 1 && isSpeaking && (
                  <Box
                    as="span"
                    display="inline-block"
                    width="0.4em"
                    height="1em"
                    bg={currentHudStyle.accentColor}
                    ml={1}
                    sx={{
                      animation: "blink 1.2s infinite",
                      "@keyframes blink": {
                        "0%, 100%": { opacity: 0 },
                        "50%": { opacity: 1 }
                      }
                    }}
                    borderRadius="1px"
                  />
                )}
              </MotionText>
            ))}
          </Box>
        </MotionBox>
      )}
    </AnimatePresence>
  );
};

export default UnifiedCaptions;
